/* Video Player - Educational Visual Canvas Edition */
import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import LessonVisualCanvas from './LessonVisualCanvas';
import type { LessonCanvasDocument } from '@/lib/ai';
import { spokenNarration } from '@/lib/narrationPerformance';
import { supabase } from '@/lib/supabase';
import { getStudyScope, isStudyScopeCurrent } from '@/lib/studyStorage';
import {
  findProgressiveSegmentAtTime,
  nextProgressiveSegment,
  pcm24kMonoToWav,
} from '@/lib/progressive-playback';
import {
  loadNarrationPlayback,
  loadNarrationPcmSegment,
  NarrationPlaybackError,
} from '@/lib/narrationPlayback';

export interface ScanVideoPlayerHandle {
  seekTo: (time: number) => void;
  setPlaying: (playing: boolean) => void;
}

interface ScanVideoPlayerProps {
  duration: number;
  chapters: Array<{ time: number; label: string }>;
  title: string;
  problem?: string;
  stepText?: string;
  summary?: string;
  result?: string;
  points?: string[];
  lessonCanvas: LessonCanvasDocument;
  chapterScripts?: string[];
  narrationProvider?: 'elevenlabs' | 'browser';
  narrationAudioUrl?: string;
  narrationGuestAccessToken?: string;
  generationId?: string;
  progressiveAudioSegments?: Array<{
    index: number;
    audioUrl: string;
    startSeconds: number;
    durationSeconds: number;
  }>;
  progressiveComplete?: boolean;
  progressiveAutoPlay?: boolean;
  progressiveFallbackUnavailable?: boolean;
  onPlayStateChange?: (playing: boolean) => void;
  onFirstPlayable?: () => void;
  onProgressiveFallbackUsed?: () => void;
  onEnded?: () => void;
  onTimeUpdate?: (time: number) => void;
  defaultPlaybackSpeed?: number;
}

const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

const formatTime = (t: number) => {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_DISTANCE = 70;
const PLAY_FLASH_MS = 650;
const CONTROLS_HIDE_DELAY = 3000;

const lockLandscape = async () => {
  try {
    const orient = (screen as unknown as { orientation?: { lock?: (o: string) => Promise<void> } }).orientation;
    if (orient?.lock) await orient.lock('landscape');
  } catch { /* silently fail */ }
};
const unlockOrientation = () => {
  try {
    const orient = (screen as unknown as { orientation?: { unlock?: () => void } }).orientation;
    if (orient?.unlock) orient.unlock();
  } catch { /* silently fail */ }
};

const ScanVideoPlayer = forwardRef<ScanVideoPlayerHandle, ScanVideoPlayerProps>(
  ({
    duration, chapters, title, lessonCanvas, chapterScripts = [], narrationProvider = 'browser',
    narrationAudioUrl, narrationGuestAccessToken, generationId, progressiveAudioSegments,
    progressiveComplete = false, progressiveAutoPlay = false, progressiveFallbackUnavailable = false,
    onPlayStateChange, onTimeUpdate, onFirstPlayable, onProgressiveFallbackUsed, onEnded,
    defaultPlaybackSpeed = 1,
  }, ref) => {
    const { resolvedTheme } = useTheme();
    const isDark = resolvedTheme === 'dark';

    /* ── State ── */
    const [isPlaying, setIsPlaying] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [showCaptions, setShowCaptions] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(defaultPlaybackSpeed);
    const [narrationAvailable, setNarrationAvailable] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(80);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [audioFailed, setAudioFailed] = useState(false);
    const [narrationError, setNarrationError] = useState<string | null>(null);
    const [progressiveFallbackActive, setProgressiveFallbackActive] = useState(false);
    const [progressiveFallbackPending, setProgressiveFallbackPending] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [seekFlash, setSeekFlash] = useState<{ side: 'left' | 'right' | null; label: string }>({ side: null, label: '' });
    const [playFlash, setPlayFlash] = useState<'play' | 'pause' | null>(null);
    const [isDraggingProgress, setIsDraggingProgress] = useState(false);
    const [seekTooltip, setSeekTooltip] = useState<{ visible: boolean; time: number; x: number }>({ visible: false, time: 0, x: 0 });
    const currentChapterIndex = chapters.reduce(
      (active, chapter, index) => (chapter.time <= currentTime ? index : active),
      0,
    );
    const onPlayStateChangeRef = useRef(onPlayStateChange);
    onPlayStateChangeRef.current = onPlayStateChange;
    const onProgressiveFallbackUsedRef = useRef(onProgressiveFallbackUsed);
    onProgressiveFallbackUsedRef.current = onProgressiveFallbackUsed;
    const currentTimeRef = useRef(currentTime);
    currentTimeRef.current = currentTime;
    const audioRef = useRef<HTMLAudioElement>(null);
    const [audioSource, setAudioSource] = useState<string>();
    const audioSourceRef = useRef(audioSource);
    audioSourceRef.current = audioSource;
    const progressiveMode = narrationProvider === 'elevenlabs' &&
      Array.isArray(progressiveAudioSegments) && !progressiveFallbackActive;
    const progressiveSegmentsKey = (progressiveAudioSegments || [])
      .map(segment => `${segment.index}:${segment.audioUrl}`)
      .join('|');
    const progressiveAudioSegmentsRef = useRef(progressiveAudioSegments);
    progressiveAudioSegmentsRef.current = progressiveAudioSegments;
    const [progressiveSources, setProgressiveSources] = useState<Record<number, string>>({});
    const [currentProgressiveIndex, setCurrentProgressiveIndex] = useState(0);
    const [isBufferingSegment, setIsBufferingSegment] = useState(false);
    const [progressiveLoadEpoch, setProgressiveLoadEpoch] = useState(0);
    const progressiveLoadersRef = useRef(new Map<number, AbortController>());
    const progressiveBlobUrlsRef = useRef(new Set<string>());
    const progressiveLoadedUrlsRef = useRef(new Map<number, string>());
    const waitingForSegmentRef = useRef<number | null>(null);
    const waitingShouldResumeRef = useRef(false);
    const pendingLocalSeekRef = useRef<number | null>(null);
    const progressiveFallbackSeekRef = useRef(0);
    const progressiveFallbackResumeRef = useRef(false);
    const progressiveFallbackReportedRef = useRef(false);
    const activeProgressiveSegment = progressiveAudioSegments?.find(segment => segment.index === currentProgressiveIndex);
    const activeProgressiveStartSeconds = activeProgressiveSegment?.startSeconds;
    const activeProgressiveDurationSeconds = activeProgressiveSegment?.durationSeconds;
    const activeAudioSource = progressiveMode
      ? progressiveSources[currentProgressiveIndex]
      : audioSource;
    const hasElevenLabsAudio = narrationProvider === 'elevenlabs' &&
      (Boolean(narrationAudioUrl) || (progressiveMode && Boolean(progressiveAudioSegments?.length))) &&
      !audioFailed;

    const activateFullAudioFallback = useCallback(() => {
      if (!narrationAudioUrl) return;
      pendingLocalSeekRef.current = progressiveFallbackSeekRef.current;
      setProgressiveFallbackPending(false);
      setProgressiveFallbackActive(true);
      setIsBufferingSegment(true);
      setAudioFailed(false);
      setNarrationAvailable(true);
      setNarrationError(null);
      const shouldResume = progressiveFallbackResumeRef.current;
      setIsPlaying(shouldResume);
      isPlayingRef.current = shouldResume;
      onPlayStateChangeRef.current?.(shouldResume);
    }, [narrationAudioUrl]);

    const requestFullAudioFallback = useCallback(() => {
      if (!progressiveMode) return false;
      const audio = audioRef.current;
      const segment = activeProgressiveSegment;
      const lessonTime = audio && segment
        ? segment.startSeconds + Math.max(0, audio.currentTime)
        : currentTimeRef.current;
      progressiveFallbackSeekRef.current = Math.min(duration, Math.max(0, lessonTime));
      progressiveFallbackResumeRef.current = isPlayingRef.current ||
        (progressiveAutoPlay && !firstPlayableReportedRef.current);
      setProgressiveFallbackPending(true);
      setIsBufferingSegment(true);
      setNarrationError(narrationAudioUrl
        ? 'Progressive playback failed. Switching to the saved full narration.'
        : 'Progressive playback failed. Preparing the saved full narration fallback.');
      if (!progressiveFallbackReportedRef.current) {
        progressiveFallbackReportedRef.current = true;
        onProgressiveFallbackUsedRef.current?.();
      }
      for (const controller of progressiveLoadersRef.current.values()) controller.abort();
      progressiveLoadersRef.current.clear();
      if (audio) audio.pause();
      setIsPlaying(false);
      isPlayingRef.current = false;
      onPlayStateChangeRef.current?.(false);
      if (narrationAudioUrl) activateFullAudioFallback();
      return true;
    }, [activeProgressiveSegment, activateFullAudioFallback, duration, narrationAudioUrl, progressiveAutoPlay, progressiveMode]);

    useEffect(() => {
      if (progressiveFallbackPending && narrationAudioUrl) {
        activateFullAudioFallback();
      } else if (progressiveFallbackPending && progressiveFallbackUnavailable) {
        setProgressiveFallbackPending(false);
        setIsBufferingSegment(false);
        setNarrationAvailable(false);
        setNarrationError('The progressive section failed and no complete narration fallback is available.');
      }
    }, [activateFullAudioFallback, narrationAudioUrl, progressiveFallbackPending, progressiveFallbackUnavailable]);

    useEffect(() => {
      const controller = new AbortController();
      const owner = getStudyScope();
      let lease: Awaited<ReturnType<typeof loadNarrationPlayback>> | undefined;
      setAudioSource(undefined);
      setAudioFailed(false);
      if (!progressiveFallbackPending) setNarrationError(null);
      if (!progressiveMode) setCurrentProgressiveIndex(0);
      if (narrationAudioUrl && generationId) {
        void (async () => {
          const { data } = await supabase.auth.getSession();
          if (controller.signal.aborted || !isStudyScopeCurrent(owner)) return;
          if ((data.session?.user.id || null) !== owner.ownerId) throw new NarrationPlaybackError('Sign in to the account that created this lesson to load its narration.');
          lease = await loadNarrationPlayback({
            audioUrl: narrationAudioUrl, generationId,
            accessToken: data.session?.access_token,
            guestAccessToken: narrationGuestAccessToken, signal: controller.signal,
          });
          if (controller.signal.aborted || !isStudyScopeCurrent(owner)) { lease.dispose(); return; }
          setAudioSource(lease.url);
        })().catch(error => {
          if (!controller.signal.aborted && isStudyScopeCurrent(owner)) {
            if (progressiveMode && progressiveFallbackPending) {
              setProgressiveFallbackPending(false);
              setIsBufferingSegment(false);
              setNarrationAvailable(false);
              setNarrationError('The progressive section failed and the saved full narration could not be loaded.');
            } else if (!progressiveMode) {
              setAudioFailed(true);
              setNarrationError(error instanceof NarrationPlaybackError ? error.message : 'Narration could not be loaded. Please try reopening the lesson.');
            }
          }
        });
      } else if (!progressiveMode) {
        setAudioFailed(true);
        setNarrationError('This saved lesson is missing its original narration reference.');
      }
      const audio = audioRef.current;
      return () => {
        controller.abort();
        lease?.dispose();
        if (audio && !progressiveMode) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
      };
    }, [narrationAudioUrl, narrationGuestAccessToken, generationId, progressiveMode, progressiveFallbackPending]);

    useEffect(() => {
      if (!progressiveMode || !generationId) return;
      const owner = getStudyScope();
      for (const segment of progressiveAudioSegmentsRef.current || []) {
        if (progressiveLoadedUrlsRef.current.has(segment.index) ||
            progressiveLoadersRef.current.has(segment.index)) continue;
        const controller = new AbortController();
        progressiveLoadersRef.current.set(segment.index, controller);
        void (async () => {
          const { data } = await supabase.auth.getSession();
          if (controller.signal.aborted || !isStudyScopeCurrent(owner) ||
              (data.session?.user.id || null) !== owner.ownerId || !owner.ownerId) {
            throw new NarrationPlaybackError('Sign in to the account that created this lesson to load its narration.');
          }
          const pcm = await loadNarrationPcmSegment({
            audioUrl: segment.audioUrl,
            generationId,
            accessToken: data.session?.access_token,
            signal: controller.signal,
          });
          if (controller.signal.aborted || !isStudyScopeCurrent(owner)) return;
          const url = URL.createObjectURL(pcm24kMonoToWav(pcm));
          progressiveBlobUrlsRef.current.add(url);
          progressiveLoadedUrlsRef.current.set(segment.index, url);
          setProgressiveSources(previous => ({ ...previous, [segment.index]: url }));
          if (waitingForSegmentRef.current === segment.index) {
            waitingForSegmentRef.current = null;
            setCurrentProgressiveIndex(segment.index);
            setCurrentTime(segment.startSeconds);
            setIsBufferingSegment(false);
            const shouldResume = waitingShouldResumeRef.current;
            waitingShouldResumeRef.current = false;
            setIsPlaying(shouldResume);
            isPlayingRef.current = shouldResume;
            onPlayStateChange?.(shouldResume);
          } else if (progressiveAutoPlay && segment.index === 0 && currentProgressiveIndex === 0) {
            setIsPlaying(true);
            isPlayingRef.current = true;
            onPlayStateChange?.(true);
          }
        })().catch(error => {
          if (!controller.signal.aborted && isStudyScopeCurrent(owner)) {
            if (!requestFullAudioFallback()) {
              setNarrationError(
                error instanceof NarrationPlaybackError ? error.message : 'A lesson section could not be loaded. Please retry playback.',
              );
              setNarrationAvailable(false);
            }
          }
        }).finally(() => {
          progressiveLoadersRef.current.delete(segment.index);
        });
      }
    }, [progressiveSegmentsKey, progressiveMode, generationId, progressiveAutoPlay, currentProgressiveIndex, progressiveLoadEpoch, onPlayStateChange, requestFullAudioFallback]);

    useEffect(() => () => {
      for (const controller of progressiveLoadersRef.current.values()) controller.abort();
      progressiveLoadersRef.current.clear();
      for (const url of progressiveBlobUrlsRef.current) URL.revokeObjectURL(url);
      progressiveBlobUrlsRef.current.clear();
      progressiveLoadedUrlsRef.current.clear();
    }, [generationId]);

    useEffect(() => {
      if (!progressiveMode && !progressiveFallbackActive) return;
      const owner = getStudyScope();
      const { data } = supabase.auth.onAuthStateChange((_event, session) => {
        if ((session?.user.id || null) === owner.ownerId) return;
        audioRef.current?.pause();
        for (const controller of progressiveLoadersRef.current.values()) controller.abort();
        progressiveLoadersRef.current.clear();
        for (const url of progressiveBlobUrlsRef.current) URL.revokeObjectURL(url);
        progressiveBlobUrlsRef.current.clear();
        progressiveLoadedUrlsRef.current.clear();
        setProgressiveSources({});
        setProgressiveFallbackPending(false);
        setIsBufferingSegment(false);
        if (audioSourceRef.current?.startsWith('blob:')) URL.revokeObjectURL(audioSourceRef.current);
        audioSourceRef.current = undefined;
        setAudioSource(undefined);
        setIsPlaying(false);
        isPlayingRef.current = false;
        setAudioFailed(true);
        setNarrationAvailable(false);
        setNarrationError('Your account changed. Reopen this lesson from the account that created it.');
        onPlayStateChangeRef.current?.(false);
      });
      return () => data.subscription.unsubscribe();
    }, [progressiveMode, progressiveFallbackActive, generationId]);

    // Update playback speed when defaultPlaybackSpeed prop changes
    useEffect(() => {
      if (defaultPlaybackSpeed && speedOptions.includes(defaultPlaybackSpeed)) {
        setPlaybackSpeed(defaultPlaybackSpeed);
      }
    }, [defaultPlaybackSpeed]);

    /* ── Refs ── */
    const videoContainerRef = useRef<HTMLDivElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showControlsAndResetTimerRef = useRef<() => void>(() => {});
    const settingsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const seekFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const playFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const volTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isPlayingRef = useRef(isPlaying);
    isPlayingRef.current = isPlaying;
    const firstPlayableReportedRef = useRef(false);
    const isDraggingRef = useRef(false);

    const tapRef = useRef<{
      x: number; y: number; time: number; moved: boolean;
      doubleTap: { lastTime: number; lastX: number; lastY: number; lastZone: 'left' | 'right' | null };
    }>({ x: 0, y: 0, time: 0, moved: false, doubleTap: { lastTime: 0, lastX: 0, lastY: 0, lastZone: null } });

    const seekToLessonTime = useCallback((requestedTime: number) => {
      if (progressiveMode) {
        const segments = progressiveAudioSegments || [];
        const availableDuration = progressiveComplete
          ? duration
          : Math.min(duration, Math.max(0, ...segments.map(segment => segment.startSeconds + segment.durationSeconds)));
        const nextTime = Math.max(0, Math.min(availableDuration, requestedTime));
        const target = findProgressiveSegmentAtTime(segments, nextTime, availableDuration);
        if (target) {
          const localTime = Math.max(0, nextTime - target.startSeconds);
          pendingLocalSeekRef.current = localTime;
          if (progressiveLoadedUrlsRef.current.has(target.index)) {
            setCurrentProgressiveIndex(target.index);
            if (audioRef.current && currentProgressiveIndex === target.index) {
              audioRef.current.currentTime = localTime;
              pendingLocalSeekRef.current = null;
            }
            setIsBufferingSegment(false);
          } else {
            waitingForSegmentRef.current = target.index;
            waitingShouldResumeRef.current = isPlayingRef.current;
            setIsBufferingSegment(true);
            setIsPlaying(false);
            isPlayingRef.current = false;
            onPlayStateChange?.(false);
          }
        }
        setCurrentTime(nextTime);
        onTimeUpdate?.(nextTime);
        return;
      }
      const nextTime = Math.max(0, Math.min(duration, requestedTime));
      if (audioRef.current) {
        audioRef.current.currentTime = nextTime;
      }
      setCurrentTime(nextTime);
      onTimeUpdate?.(nextTime);
    }, [duration, progressiveMode, progressiveAudioSegments, progressiveComplete, currentProgressiveIndex, onTimeUpdate, onPlayStateChange]);

    /* ── Imperative handle ── */
    useImperativeHandle(ref, () => ({
      seekTo: seekToLessonTime,
      setPlaying: (playing: boolean) => {
        setIsPlaying(playing);
        isPlayingRef.current = playing;
        onPlayStateChange?.(playing);
        showPlayFlash(playing ? 'play' : 'pause');
        showControlsAndResetTimerRef.current();
      },
    }), [seekToLessonTime, onPlayStateChange]);

    /* Generate simple captions from chapters */
    const narratorScript = chapters.map((c, index) => ({ time: c.time, text: spokenNarration(chapterScripts[index] || c.label) }));
    const currentSubtitle = narratorScript
      .slice()
      .reverse()
      .find((s) => currentTime >= s.time);
    const subtitleText = currentSubtitle?.text || '';

    /* ── Theme tokens ── */
    const tk = {
      videoBg: 'bg-white',
      fullscreenBg: 'bg-white',
      ctrlBg: isDark ? 'bg-black/50' : 'bg-white/40',
      ctrlBorder: isDark ? 'border-white/[0.06]' : 'border-black/[0.06]',
      ctrlText: isDark ? 'text-white/70' : 'text-black/60',
      ctrlTextHover: isDark ? 'text-white' : 'text-black',
      ctrlIcon: isDark ? 'text-white/70' : 'text-black/60',
      overlay: isDark ? 'bg-gradient-to-t from-black/80 via-black/30 to-transparent' : 'bg-gradient-to-t from-black/60 via-black/20 to-transparent',
      overlayFullscreen: isDark ? 'bg-black/40' : 'bg-black/20',
      textPrimary: isDark ? 'text-white' : 'text-neutral-900',
      textSecondary: isDark ? 'text-white/50' : 'text-black/50',
      textMuted: isDark ? 'text-white/30' : 'text-black/30',
      textFaint: isDark ? 'text-white/20' : 'text-black/20',
      progressTrack: isDark ? 'bg-white/[0.12]' : 'bg-black/10',
      progressFill: isDark ? 'bg-white' : 'bg-black',
      progressBuffered: isDark ? 'bg-white/[0.06]' : 'bg-black/5',
      btnPrimary: isDark ? 'bg-white text-black' : 'bg-black text-white',
      btnGhost: isDark ? 'bg-white/[0.08] border-white/[0.12]' : 'bg-black/[0.06] border-black/[0.08]',
      brandText: isDark ? 'text-white/40' : 'text-black/40',
      ccOn: isDark ? 'bg-white/20 border-white/20 text-white' : 'bg-black/20 border-black/20 text-black',
      ccOff: isDark ? 'bg-black/50 border-white/[0.06] text-white/50' : 'bg-white/40 border-black/[0.06] text-black/50',
      subtitleBg: isDark ? 'bg-black/50' : 'bg-white/70',
      subtitleText: isDark ? 'text-white' : 'text-black',
      sheetBg: isDark ? 'bg-[#161618]' : 'bg-white',
      sheetBackdrop: isDark ? 'bg-black/70' : 'bg-black/40',
      settingsLabel: isDark ? 'text-white/50' : 'text-black/50',
      settingsOption: isDark ? 'text-white/50 hover:text-white/70 hover:bg-white/[0.04]' : 'text-black/50 hover:text-black/70 hover:bg-black/[0.04]',
      settingsOptionActive: isDark ? 'bg-white text-black' : 'bg-black text-white',
      settingsOptionInactive: isDark ? 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08]' : 'bg-black/[0.04] text-black/40 hover:bg-black/[0.08]',
      toggleTrackOn: isDark ? 'bg-white' : 'bg-black',
      toggleTrackOff: isDark ? 'bg-white/[0.12]' : 'bg-black/[0.12]',
      toggleThumb: isDark ? 'bg-black' : 'bg-white',
      settingsBorder: isDark ? 'border-white/[0.04]' : 'border-black/[0.04]',
      settingsIcon: isDark ? 'text-white/40' : 'text-black/40',
      settingsIconMuted: isDark ? 'text-white/20' : 'text-black/20',
      seekFlashBg: isDark ? 'bg-black/60 border-white/10' : 'bg-white/70 border-black/10',
      seekFlashText: isDark ? 'text-white' : 'text-black',
      seekFlashLabelBg: isDark ? 'bg-black/50' : 'bg-white/70',
      playFlashBg: isDark ? 'bg-black/50' : 'bg-white/70',
      playFlashIcon: isDark ? 'text-white' : 'text-black',
      bottomBarBg: 'bg-white/95',
      bottomBarBgFullscreen: 'bg-white/95',
      playBtnBg: 'bg-white border-black/20',
      playBtnIcon: 'text-black',
      timeText: 'text-black/70',
      speedText: 'text-black/70',
      externalTrack: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]',
      muteIcon: isDark ? 'text-red-400' : 'text-red-500',
      volHigh: isDark ? 'text-white' : 'text-black',
      volLow: isDark ? 'text-white/80' : 'text-black/70',
      tooltipBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    /* ── Helpers ── */
    const hideAllControls = useCallback(() => {
      setShowControls(false);
      setShowSettings(false);
      setShowVolumeSlider(false);
    }, []);

    const showControlsAndResetTimer = useCallback(() => {
      setShowControls(true);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
      if (!isPlayingRef.current) return;
      controlsTimerRef.current = setTimeout(() => {
        if (isPlayingRef.current) {
          hideAllControls();
        }
      }, CONTROLS_HIDE_DELAY);
    }, [hideAllControls]);
    showControlsAndResetTimerRef.current = showControlsAndResetTimer;

    const showPlayFlash = (action: 'play' | 'pause') => {
      if (playFlashTimeoutRef.current) clearTimeout(playFlashTimeoutRef.current);
      setPlayFlash(action);
      playFlashTimeoutRef.current = setTimeout(() => setPlayFlash(null), PLAY_FLASH_MS);
    };

    const showSeekFlash = (side: 'left' | 'right', label: string) => {
      if (seekFlashTimeoutRef.current) clearTimeout(seekFlashTimeoutRef.current);
      setSeekFlash({ side, label });
      seekFlashTimeoutRef.current = setTimeout(() => setSeekFlash({ side: null, label: '' }), 700);
    };

    const togglePlay = useCallback(() => {
      setIsPlaying((p) => {
        const next = !p;
        if (progressiveMode) {
          waitingShouldResumeRef.current = next;
          if (next && !activeAudioSource) setIsBufferingSegment(true);
        }
        isPlayingRef.current = next;
        showPlayFlash(next ? 'play' : 'pause');
        onPlayStateChange?.(next);
        return next;
      });
    }, [onPlayStateChange, progressiveMode, activeAudioSource]);

    const handleSeek = useCallback((clientX: number, el?: HTMLDivElement) => {
      const bar = el || progressBarRef.current;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newTime = pct * duration;
      seekToLessonTime(newTime);
    }, [duration, seekToLessonTime]);

    const getTapZone = (clientX: number, rect: DOMRect): 'left' | 'center' | 'right' => {
      const rx = (clientX - rect.left) / rect.width;
      if (rx < 0.33) return 'left';
      if (rx > 0.67) return 'right';
      return 'center';
    };

    /* ElevenLabs audio is the authoritative clock whenever it is available. */
    useEffect(() => {
      const audio = audioRef.current;
      if (!hasElevenLabsAudio || !audio || !activeAudioSource) return;
      audio.playbackRate = playbackSpeed;
      audio.volume = Math.max(0, Math.min(1, volume / 100));
      audio.muted = isMuted;
      if (!isPlaying) {
        audio.pause();
        return;
      }
      void audio.play().then(() => {
        setNarrationAvailable(true);
        setNarrationError(null);
        if (progressiveMode && !firstPlayableReportedRef.current) {
          firstPlayableReportedRef.current = true;
          onFirstPlayable?.();
        }
      }).catch(error => {
        if (error?.name === 'NotAllowedError') {
          setIsPlaying(false);
          isPlayingRef.current = false;
          onPlayStateChange?.(false);
          setNarrationAvailable(false);
          setNarrationError('Tap Play to allow audio playback.');
        } else if (progressiveMode && requestFullAudioFallback()) {
          return;
        } else {
          setAudioFailed(true);
          setNarrationError('The browser could not decode or play the narration audio.');
        }
      });
    }, [hasElevenLabsAudio, activeAudioSource, isPlaying, isMuted, playbackSpeed, volume, onPlayStateChange, progressiveMode, onFirstPlayable, requestFullAudioFallback]);

    useEffect(() => {
      const audio = audioRef.current;
      if (!hasElevenLabsAudio || !audio || !activeAudioSource || !isPlaying) return;
      let frame = 0;
      const updateFromAudio = () => {
        if (!isDraggingRef.current) {
          const next = Math.min(duration,
            activeProgressiveStartSeconds !== undefined && activeProgressiveDurationSeconds !== undefined
              ? Math.max(0, activeProgressiveStartSeconds + Math.max(0, Math.min(activeProgressiveDurationSeconds, audio.currentTime)))
              : audio.currentTime);
          setCurrentTime(next);
          onTimeUpdate?.(next);
        }
        if (!audio.ended && !audio.paused) frame = requestAnimationFrame(updateFromAudio);
      };
      frame = requestAnimationFrame(updateFromAudio);
      return () => cancelAnimationFrame(frame);
    }, [hasElevenLabsAudio, activeAudioSource, activeProgressiveStartSeconds, activeProgressiveDurationSeconds, isPlaying, duration, onTimeUpdate]);

    /* Preserve visual-only playback when audio is unavailable, without substituting a voice. */
    useEffect(() => {
      if (progressiveMode || hasElevenLabsAudio || !isPlaying) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
      intervalRef.current = setInterval(() => {
        setCurrentTime((t) => {
          const next = t + 1 * playbackSpeed;
          if (next >= duration) {
            setIsPlaying(false);
            isPlayingRef.current = false;
            onPlayStateChange?.(false);
            onTimeUpdate?.(duration);
            return duration;
          }
          onTimeUpdate?.(next);
          return next;
        });
      }, 1000);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [hasElevenLabsAudio, progressiveMode, isPlaying, playbackSpeed, duration, onPlayStateChange, onTimeUpdate]);

    /* Teacher narration comes only from the server-generated ElevenLabs audio.
       Missing or failed audio must never switch to a different browser voice. */
    useEffect(() => {
      setNarrationAvailable(hasElevenLabsAudio);
    }, [hasElevenLabsAudio]);

    /* ── Controls auto-hide ── */
    useEffect(() => {
      showControlsAndResetTimer();
      return () => { if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current); };
    }, [showControlsAndResetTimer]);

    /* ── Fullscreen listeners ── */
    useEffect(() => {
      const onChange = () => {
        const fs = !!document.fullscreenElement;
        setIsFullscreen(fs);
        if (!fs) unlockOrientation();
      };
      document.addEventListener('fullscreenchange', onChange);
      return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = useCallback(async () => {
      const el = videoContainerRef.current;
      if (!el) return;
      try {
        if (!document.fullscreenElement) {
          await el.requestFullscreen();
          await lockLandscape();
        } else {
          await document.exitFullscreen();
          unlockOrientation();
        }
      } catch {
        setIsFullscreen((p) => { const next = !p; if (next) lockLandscape(); else unlockOrientation(); return next; });
      }
    }, []);

    /* ── Settings auto-hide ── */
    useEffect(() => {
      if (showSettings) {
        if (settingsTimerRef.current) clearTimeout(settingsTimerRef.current);
        settingsTimerRef.current = setTimeout(() => {
          setShowSettings(false);
        }, 8000);
      }
      return () => { if (settingsTimerRef.current) clearTimeout(settingsTimerRef.current); };
    }, [showSettings]);

    /* ── Volume timer ── */
    useEffect(() => {
      if (showVolumeSlider) {
        if (volTimerRef.current) clearTimeout(volTimerRef.current);
        volTimerRef.current = setTimeout(() => setShowVolumeSlider(false), 4000);
      }
      return () => { if (volTimerRef.current) clearTimeout(volTimerRef.current); };
    }, [showVolumeSlider, volume]);

    /* ── Container pointer events ── */
    const handleContainerPointerDown = (e: React.PointerEvent) => {
      const rect = videoContainerRef.current?.getBoundingClientRect();
      if (!rect) return;
      tapRef.current.x = e.clientX;
      tapRef.current.y = e.clientY;
      tapRef.current.time = Date.now();
      tapRef.current.moved = false;
    };

    const handleContainerPointerMove = (e: React.PointerEvent) => {
      const dx = e.clientX - tapRef.current.x;
      const dy = e.clientY - tapRef.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > 10) tapRef.current.moved = true;
    };

    const handleContainerPointerUp = (e: React.PointerEvent) => {
      if (tapRef.current.moved) return;
      const rect = videoContainerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const zone = getTapZone(e.clientX, rect);
      const now = Date.now();
      const isTouch = e.pointerType === 'touch';

      if (zone === 'center') {
        if (showSettings) { setShowSettings(false); showControlsAndResetTimer(); return; }
        if (showVolumeSlider) { setShowVolumeSlider(false); showControlsAndResetTimer(); return; }
        setIsPlaying((p) => { const next = !p; isPlayingRef.current = next; showPlayFlash(next ? 'play' : 'pause'); onPlayStateChange?.(next); return next; });
        showControlsAndResetTimer();
        return;
      }

      const dt = tapRef.current.doubleTap;
      const dx = e.clientX - dt.lastX;
      const dy = e.clientY - dt.lastY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const isDoubleTap = dt.lastZone === zone && now - dt.lastTime < DOUBLE_TAP_MS && dist < DOUBLE_TAP_DISTANCE;

      dt.lastTime = now;
      dt.lastX = e.clientX;
      dt.lastY = e.clientY;
      dt.lastZone = zone;

      if (isDoubleTap && isTouch) {
        const amt = 10;
        if (zone === 'left') {
          const nt = Math.max(0, currentTime - amt);
          seekToLessonTime(nt); showSeekFlash('left', `-${amt}s`);
        } else {
          const nt = Math.min(duration, currentTime + amt);
          seekToLessonTime(nt); showSeekFlash('right', `+${amt}s`);
        }
        showControlsAndResetTimer();
        dt.lastTime = 0; dt.lastZone = null;
      } else if (!isTouch) {
        const amt = 10;
        if (zone === 'left') {
          const nt = Math.max(0, currentTime - amt);
          seekToLessonTime(nt); showSeekFlash('left', `-${amt}s`);
        } else {
          const nt = Math.min(duration, currentTime + amt);
          seekToLessonTime(nt); showSeekFlash('right', `+${amt}s`);
        }
        showControlsAndResetTimer();
      } else {
        showControlsAndResetTimer();
      }
    };

    /* ── Progress bar pointer events ── */
    const handleProgressPointerDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const el = e.currentTarget as HTMLDivElement;
      el.setPointerCapture(e.pointerId);
      isDraggingRef.current = true;
      setIsDraggingProgress(true);
      handleSeek(e.clientX, el);
      showControlsAndResetTimer();
    };

    const handleProgressPointerMove = (e: React.PointerEvent) => {
      if (!isDraggingRef.current) {
        const rect = progressBarRef.current?.getBoundingClientRect();
        if (rect) {
          const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          setSeekTooltip({ visible: true, time: Math.round(pct * duration), x: e.clientX - rect.left });
        }
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      const el = e.currentTarget as HTMLDivElement;
      handleSeek(e.clientX, el);
      const rect = el.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setSeekTooltip({ visible: true, time: Math.round(pct * duration), x: e.clientX - rect.left });
    };

    const handleProgressPointerUp = (e: React.PointerEvent) => {
      e.stopPropagation();
      isDraggingRef.current = false;
      setIsDraggingProgress(false);
      setSeekTooltip((t) => ({ ...t, visible: false }));
      showControlsAndResetTimer();
    };

    const handleProgressPointerLeave = () => {
      setSeekTooltip((t) => ({ ...t, visible: false }));
    };

    /* ── Outside progress bar (non-fullscreen) ── */
    const handleOutsideProgressDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const el = e.currentTarget as HTMLDivElement;
      el.setPointerCapture(e.pointerId);
      isDraggingRef.current = true;
      setIsDraggingProgress(true);
      const rect = el.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const nt = pct * duration;
      seekToLessonTime(nt);
      showControlsAndResetTimer();
    };

    const handleOutsideProgressMove = (e: React.PointerEvent) => {
      if (!isDraggingRef.current) return;
      e.stopPropagation();
      const el = e.currentTarget as HTMLDivElement;
      const rect = el.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const nt = pct * duration;
      seekToLessonTime(nt);
    };

    const handleOutsideProgressUp = (e: React.PointerEvent) => {
      e.stopPropagation();
      isDraggingRef.current = false;
      setIsDraggingProgress(false);
      showControlsAndResetTimer();
    };

    const handleNarrationSegmentEnded = () => {
      if (!progressiveMode) {
        setCurrentTime(duration);
        onTimeUpdate?.(duration);
        setIsPlaying(false);
        isPlayingRef.current = false;
        onPlayStateChange?.(false);
        onEnded?.();
        return;
      }
      const nextSegment = nextProgressiveSegment(progressiveAudioSegments || [], currentProgressiveIndex);
      if (!nextSegment && progressiveComplete) {
        setCurrentTime(duration);
        onTimeUpdate?.(duration);
        setIsPlaying(false);
        isPlayingRef.current = false;
        onPlayStateChange?.(false);
        setIsBufferingSegment(false);
        onEnded?.();
        return;
      }
      if (nextSegment && progressiveLoadedUrlsRef.current.has(nextSegment.index)) {
        pendingLocalSeekRef.current = 0;
        setCurrentProgressiveIndex(nextSegment.index);
        setCurrentTime(nextSegment.startSeconds);
        setIsBufferingSegment(false);
        setIsPlaying(true);
        isPlayingRef.current = true;
        onPlayStateChange?.(true);
        return;
      }
      const nextIndex = nextSegment?.index ?? currentProgressiveIndex + 1;
      waitingForSegmentRef.current = nextIndex;
      waitingShouldResumeRef.current = true;
      setIsBufferingSegment(true);
      setIsPlaying(false);
      isPlayingRef.current = false;
      onPlayStateChange?.(false);
    };

    const retryProgressiveSegment = () => {
      const failedIndex = currentProgressiveIndex;
      const url = progressiveLoadedUrlsRef.current.get(failedIndex);
      if (url) {
        URL.revokeObjectURL(url);
        progressiveBlobUrlsRef.current.delete(url);
        progressiveLoadedUrlsRef.current.delete(failedIndex);
        setProgressiveSources(previous => {
          const next = { ...previous };
          delete next[failedIndex];
          return next;
        });
      }
      setAudioFailed(false);
      setNarrationAvailable(true);
      setNarrationError(null);
      waitingForSegmentRef.current = failedIndex;
      waitingShouldResumeRef.current = true;
      setIsBufferingSegment(true);
      setProgressiveLoadEpoch(value => value + 1);
    };

    return (
      <div className={`w-full ${isFullscreen ? `fixed inset-0 z-[100] ${tk.fullscreenBg}` : ''}`}>
        {narrationProvider === 'elevenlabs' && (narrationAudioUrl || progressiveMode) && (
          <audio
            ref={audioRef}
            src={activeAudioSource}
            preload="auto"
            onLoadedMetadata={() => {
              if (audioRef.current) {
                const pendingTime = pendingLocalSeekRef.current;
                audioRef.current.currentTime = pendingTime !== null
                  ? pendingTime
                  : Math.max(0, currentTime - (activeProgressiveSegment?.startSeconds || 0));
                pendingLocalSeekRef.current = null;
                if (isPlayingRef.current) void audioRef.current.play().catch(() => {});
              }
            }}
            onEnded={handleNarrationSegmentEnded}
            onError={() => {
              if (progressiveMode) {
                if (!requestFullAudioFallback()) {
                  setIsPlaying(false);
                  isPlayingRef.current = false;
                  setNarrationAvailable(false);
                  setNarrationError('This lesson section could not be decoded. Retry loading the saved section.');
                  onPlayStateChange?.(false);
                }
              } else {
                setAudioFailed(true);
                setNarrationError('The browser could not decode or play the narration audio.');
              }
            }}
          />
        )}
        {/* Video Container */}
        <div
          ref={videoContainerRef}
          className={`relative overflow-hidden select-none ${tk.videoBg} ${isFullscreen ? 'w-full h-full rounded-none border-0' : `w-full aspect-video rounded-2xl border-2 ${isDark ? 'border-white/75' : 'border-black'}`}`}
          onPointerDown={handleContainerPointerDown}
          onPointerMove={handleContainerPointerMove}
          onPointerUp={handleContainerPointerUp}
          style={{ cursor: 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        >
          {/* ── Educational Visual Canvas ── */}
          <LessonVisualCanvas
            currentTime={currentTime}
            lessonCanvas={lessonCanvas}
            title={title}
          />
          {isBufferingSegment && (
            <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full bg-black/65 px-3 py-1.5 text-xs text-white" role="status">
              Preparing the next teaching section…
            </div>
          )}

          {/* Play/Pause Flash */}
          {playFlash && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none" style={{ animation: 'playFlashIn 0.18s ease-out forwards' }}>
              <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full ${tk.playFlashBg} flex items-center justify-center backdrop-blur-sm`}>
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
                  <i className={`${playFlash === 'play' ? 'ri-play-fill' : 'ri-pause-fill'} ${tk.playFlashIcon} text-2xl md:text-3xl`} />
                </div>
              </div>
            </div>
          )}

          {/* Seek Flash */}
          {seekFlash.side && (
            <div
              className={`absolute top-1/2 -translate-y-1/2 pointer-events-none z-20 ${seekFlash.side === 'left' ? 'left-4 md:left-6' : 'right-4 md:right-6'}`}
              style={{ animation: 'seekFlashIn 0.2s ease-out, seekFlashOut 0.5s ease-in 0.2s forwards' }}
            >
              <div className="flex flex-col items-center gap-1">
                <div className={`w-10 h-10 rounded-full ${tk.seekFlashBg} border flex items-center justify-center`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${seekFlash.side === 'left' ? 'ri-rewind-start-line' : 'ri-speed-line'} ${tk.seekFlashText} text-sm`} />
                  </div>
                </div>
                <span className={`${tk.seekFlashText} text-[11px] font-semibold ${tk.seekFlashLabelBg} px-2 py-0.5 rounded-full`}>
                  {seekFlash.label}
                </span>
              </div>
            </div>
          )}

          {/* Center Play Button (paused, no flash) */}
          {!isPlaying && !playFlash && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <button
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setIsPlaying(true);
                  isPlayingRef.current = true;
                  onPlayStateChange?.(true);
                  showPlayFlash('play');
                  showControlsAndResetTimer();
                }}
                className={`w-14 h-14 md:w-18 md:h-18 rounded-full ${tk.playBtnBg} flex items-center justify-center cursor-pointer active:scale-[0.88] transition-transform duration-150`}
              >
                <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center">
                  <i className={`ri-play-fill ${tk.playBtnIcon} text-lg md:text-xl ml-0.5`} />
                </div>
              </button>
            </div>
          )}

          {/* ── Top Controls ── */}
          {showControls && (
            <div className="absolute top-0 left-0 right-0 flex items-center justify-end gap-1 px-2.5 pt-2.5 pb-6 z-20" style={{ animation: 'fadeIn 0.15s ease-out' }}>
              {/* Volume */}
              <div className="relative">
                <button
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setIsMuted((m) => !m);
                    setShowVolumeSlider((v) => !v);
                    setShowSettings(false);
                    showControlsAndResetTimer();
                  }}
                  className={`w-8 h-8 rounded-lg ${tk.ctrlBg} ${tk.ctrlBorder} border flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${
                      isMuted ? `ri-volume-mute-line ${tk.muteIcon}` : volume > 50 ? `ri-volume-up-line ${tk.volHigh}` : volume > 0 ? `ri-volume-down-line ${tk.volLow}` : `ri-volume-mute-line ${tk.muteIcon}`
                    } text-xs transition-all duration-150`} />
                  </div>
                </button>
                {showVolumeSlider && (
                  <div className={`absolute top-full mt-1.5 left-1/2 -translate-x-1/2 ${isDark ? 'bg-[#1C1D21]' : 'bg-white'} ${tk.ctrlBorder} border rounded-xl px-2.5 py-2.5 w-9 flex flex-col items-center gap-1.5 z-30 shadow-lg`} style={{ animation: 'fadeIn 0.15s ease-out' }}>
                    <div className={`h-20 w-[3px] ${tk.externalTrack} rounded-full relative overflow-hidden`}>
                      <div className={`absolute bottom-0 left-0 right-0 ${tk.progressFill} rounded-full transition-all duration-150`} style={{ height: `${isMuted ? 0 : volume}%` }} />
                    </div>
                    <span className={`${tk.textMuted} text-[9px] font-medium`}>{isMuted ? '0' : volume}%</span>
                  </div>
                )}
              </div>

              {/* CC */}
              <button
                onPointerDown={(e) => { e.stopPropagation(); setShowCaptions((c) => !c); showControlsAndResetTimer(); }}
                className={`w-8 h-8 rounded-lg border flex items-center justify-center cursor-pointer active:scale-90 transition-all duration-200 ${showCaptions ? tk.ccOn : tk.ccOff}`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <span className="text-[9px] font-bold">CC</span>
                </div>
              </button>

              {/* Settings */}
              <button
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setShowSettings((s) => !s);
                  setShowVolumeSlider(false);
                  showControlsAndResetTimer();
                }}
                className={`w-8 h-8 rounded-lg border flex items-center justify-center cursor-pointer active:scale-90 transition-all duration-200 ${
                  showSettings ? (isDark ? 'bg-white/20 border-white/20 text-white' : 'bg-black/20 border-black/20 text-black') : `${tk.ctrlBg} ${tk.ctrlBorder} ${tk.ctrlText}`
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-settings-4-line text-xs" />
                </div>
              </button>

              {/* Fullscreen - single button, dynamic icon */}
              <button
                onPointerDown={(e) => { e.stopPropagation(); toggleFullscreen(); showControlsAndResetTimer(); }}
                className={`w-8 h-8 rounded-lg ${tk.ctrlBg} ${tk.ctrlBorder} border flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} ${tk.ctrlIcon} text-xs`} />
                </div>
              </button>
            </div>
          )}

          {/* LAVYN Branding */}
          {showControls && (
            <div className={`absolute bottom-10 right-3 flex items-center gap-1 z-10 ${tk.brandText}`} style={{ animation: 'fadeIn 0.3s ease-out 0.1s both' }}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-sparkling-line text-[10px]" />
              </div>
              <span className="text-[10px] font-medium tracking-wide">LAVYN Tutor</span>
            </div>
          )}
          {!narrationAvailable && (
            <div className="absolute left-3 right-3 top-12 z-20 rounded-xl border border-amber-400/30 bg-amber-950/85 px-3 py-2 text-center text-[10px] font-medium text-amber-100 backdrop-blur">
              {narrationError || 'Narration playback is unavailable.'}
              {progressiveMode ? (
                <>
                  <span className="ml-1">Visual playback is paused to keep it synchronized.</span>
                  {!audioFailed && narrationError !== 'Tap Play to allow audio playback.' && (
                    <button type="button" onClick={retryProgressiveSegment} className="ml-2 underline">Retry section</button>
                  )}
                </>
              ) : (
                <span className="ml-1">The visual lesson, captions, editing, and controls still work.</span>
              )}
            </div>
          )}

          {/* Subtitles */}
          {showCaptions && subtitleText && (
            <div className="absolute bottom-14 left-0 right-0 flex items-center justify-center px-6 z-10" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <p className={`${tk.subtitleText} text-xs font-medium text-center leading-relaxed ${tk.subtitleBg} px-3 py-1.5 rounded-lg backdrop-blur-sm`}>
                {subtitleText}
              </p>
            </div>
          )}

          {/* ── Bottom Controls Bar ── */}
          {showControls && (
            <div className={`absolute bottom-0 left-0 right-0 px-2.5 pb-1.5 pt-6 ${isFullscreen ? tk.bottomBarBgFullscreen : tk.bottomBarBg} z-20`} style={{ animation: 'fadeIn 0.15s ease-out' }}>
              {/* Progress Bar */}
              <div
                ref={progressBarRef}
                className={`relative h-1 ${tk.progressTrack} rounded-full mb-1.5 cursor-pointer group`}
                onPointerDown={handleProgressPointerDown}
                onPointerMove={handleProgressPointerMove}
                onPointerUp={handleProgressPointerUp}
                onPointerLeave={handleProgressPointerLeave}
              >
                <div className={`absolute top-0 left-0 h-full ${tk.progressFill} rounded-full ${isDraggingProgress ? '' : 'transition-all duration-75'}`} style={{ width: `${progress}%` }} />
                <div className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 ${tk.progressFill} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-md`} style={{ left: `calc(${progress}% - 5px)` }} />
                <div className={`absolute top-0 left-0 h-full ${tk.progressBuffered} rounded-full`} style={{ width: `${Math.min(100, progress + 25)}%` }} />
                {seekTooltip.visible && (
                  <div
                    className={`absolute -top-7 px-1.5 py-0.5 rounded ${tk.tooltipBg} ${tk.ctrlBorder} border text-[10px] font-medium ${tk.textPrimary} whitespace-nowrap shadow-md`}
                    style={{ left: `${Math.max(0, Math.min(100, (seekTooltip.x / (progressBarRef.current?.getBoundingClientRect().width || 1)) * 100))}%`, transform: 'translateX(-50%)' }}
                  >
                    {formatTime(seekTooltip.time)}
                  </div>
                )}
              </div>

              {/* Time & Controls Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <button
                    onPointerDown={(e) => { e.stopPropagation(); togglePlay(); showControlsAndResetTimer(); }}
                    className={`w-7 h-7 rounded-full ${isDark ? 'bg-white/[0.08] hover:bg-white/[0.15]' : 'bg-black/[0.08] hover:bg-black/[0.15]'} flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} ${tk.playBtnIcon} text-[10px]`} />
                    </div>
                  </button>
                  <span className={`${tk.timeText} text-[10px] font-medium tabular-nums`}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                <span className={`${tk.speedText} text-[10px] font-medium`}>{playbackSpeed}x</span>
              </div>
            </div>
          )}
        </div>

        {/* Outside Progress Bar (non-fullscreen) */}
        {!isFullscreen && (
          <div className="mt-2 px-1">
            <div
              className={`relative h-[2px] ${tk.externalTrack} rounded-full overflow-hidden cursor-pointer`}
              onPointerDown={handleOutsideProgressDown}
              onPointerMove={handleOutsideProgressMove}
              onPointerUp={handleOutsideProgressUp}
            >
              <div className={`h-full ${tk.progressFill} rounded-full ${isDraggingProgress ? '' : 'transition-all duration-150'}`} style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* ── Settings Panel ── */}
        {showSettings && (
          <div className="absolute inset-0 z-[110] pointer-events-auto">
            <div className="absolute inset-0 bg-black/30" onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => { e.stopPropagation(); setShowSettings(false); }} />
            <div
              className={`absolute ${isFullscreen ? 'right-0 top-0 bottom-0 w-[260px] md:w-[300px]' : 'bottom-0 left-0 right-0'} ${tk.sheetBg} overflow-hidden flex flex-col ${isFullscreen ? 'border-l ' + tk.settingsBorder : 'rounded-t-3xl'}`}
              style={{ animation: isFullscreen ? 'slideInRight 0.3s cubic-bezier(0.32, 0.72, 0, 1) forwards' : 'slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1) forwards', maxHeight: isFullscreen ? '100%' : '80vh' }}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
            >
              {!isFullscreen && (
                <div className="flex justify-center pt-3 pb-1" onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => { e.stopPropagation(); setShowSettings(false); }}>
                  <div className={`w-10 h-1 ${isDark ? 'bg-white/20' : 'bg-black/20'} rounded-full`} />
                </div>
              )}
              <div className="flex items-center justify-between px-5 pt-2 pb-3 flex-shrink-0">
                <h3 className={`${tk.textPrimary} text-sm font-semibold`}>Settings</h3>
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => { e.stopPropagation(); setShowSettings(false); }}
                  className={`w-8 h-8 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'} flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-close-line ${isDark ? 'text-white/60' : 'text-black/60'} text-xs`} />
                  </div>
                </button>
              </div>
              <div className="overflow-y-auto overscroll-contain touch-pan-y flex-1 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="px-5 pb-6 pt-2 space-y-4">
                  {/* Playback Speed */}
                  <div>
                    <p className={`${tk.settingsLabel} text-[10px] font-medium uppercase tracking-wider mb-2`}>Playback Speed</p>
                    <div className="flex gap-1.5">
                      {speedOptions.map((s) => (
                        <button
                          key={s}
                          onPointerDown={(e) => { e.stopPropagation(); setPlaybackSpeed(s); }}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer ${playbackSpeed === s ? tk.settingsOptionActive : tk.settingsOptionInactive}`}
                        >
                          {s}x
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Volume */}
                  <div className={`border-t ${tk.settingsBorder} pt-4`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`${tk.settingsLabel} text-[10px] font-medium uppercase tracking-wider`}>Volume</p>
                      <span className={`${tk.settingsLabel} text-[10px] font-medium tabular-nums`}>{isMuted ? 0 : volume}%</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <button
                        onPointerDown={(e) => { e.stopPropagation(); setIsMuted((m) => !m); }}
                        className="w-7 h-7 flex items-center justify-center cursor-pointer flex-shrink-0"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${isMuted ? 'ri-volume-mute-line' : volume > 50 ? 'ri-volume-up-line' : volume > 0 ? 'ri-volume-down-line' : 'ri-volume-mute-line'} ${tk.settingsIcon} text-xs`} />
                        </div>
                      </button>
                      <div
                        ref={(el) => {
                          if (!el) return;
                          const rect = el.getBoundingClientRect();
                          const handleVolPointer = (e: PointerEvent) => {
                            const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                            setVolume(Math.round(pct * 100));
                            setIsMuted(false);
                          };
                          const onDown = (e: PointerEvent) => {
                            el.setPointerCapture(e.pointerId);
                            handleVolPointer(e);
                            const onMove = (ev: PointerEvent) => { handleVolPointer(ev); };
                            const onUp = () => { el.removeEventListener('pointermove', onMove); el.removeEventListener('pointerup', onUp); showControlsAndResetTimer(); };
                            el.addEventListener('pointermove', onMove);
                            el.addEventListener('pointerup', onUp);
                          };
                          el.addEventListener('pointerdown', onDown);
                        }}
                        className={`flex-1 h-1.5 ${tk.externalTrack} rounded-full relative overflow-hidden cursor-pointer`}
                      >
                        <div className={`absolute top-0 left-0 h-full ${tk.progressFill} rounded-full transition-all duration-75`} style={{ width: `${isMuted ? 0 : volume}%` }} />
                      </div>
                    </div>
                  </div>
                  {/* Toggles */}
                  <div className={`border-t ${tk.settingsBorder} pt-4 space-y-3`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`ri-closed-captioning-line ${tk.settingsIcon} text-xs`} />
                        </div>
                        <span className={`${isDark ? 'text-white/70' : 'text-black/70'} text-xs font-medium`}>Captions</span>
                      </div>
                      <button
                        onPointerDown={(e) => { e.stopPropagation(); setShowCaptions((c) => !c); }}
                        className={`w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer relative ${showCaptions ? tk.toggleTrackOn : tk.toggleTrackOff}`}
                      >
                        <div className={`absolute top-[2px] w-[20px] h-[20px] rounded-full ${tk.toggleThumb} transition-all duration-200 ${showCaptions ? 'left-[22px]' : 'left-[2px]'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes seekFlashIn { from { opacity: 0; transform: translateY(-50%) scale(0.6); } to { opacity: 1; transform: translateY(-50%) scale(1); } }
          @keyframes seekFlashOut { from { opacity: 1; transform: translateY(-50%) scale(1); } to { opacity: 0; transform: translateY(-50%) scale(0.6); } }
          @keyframes playFlashIn { from { opacity: 0; transform: scale(0.5); } 40% { opacity: 1; transform: scale(1.08); } 100% { opacity: 0.85; transform: scale(1); } }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
          @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>
      </div>
    );
  }
);

ScanVideoPlayer.displayName = 'ScanVideoPlayer';

export default ScanVideoPlayer;